import { useQueries, useQuery, type Query } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Panel } from '../../../components/Panel'
import { LadderBars } from '../../../components/charts/LadderBars'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { GroupNote } from './GroupNote'
import { tileValueText } from '../../../lib/deskGroup'
import type { DeskGroup } from '../../../lib/deskPeriod'
import { useDeskState } from './useDeskState'
import { deskApi, type Compared, type DeskPub, type MoverList, type Tile } from '../../../api/visitorsDesk'
import { visitorsApi } from '../../../api/visitors'
import { changeText, fmtDay, lastYearText, pubLabel, rateWithCount, verdictText } from '../../../lib/deskFormat'
import { changeHref, ladderRows, nowLinks, shapeBars, stepUpText, sumNow } from '../../../lib/deskToday'
import { positionChange } from '../../../lib/deskSearch'
import { deskPath } from '../../../lib/deskTabs'
import { fmtInt } from '../../../lib/format'

const poll = (ms: number) => (q: Query<any, any, any, any>) => (q.state.status === 'error' ? false : ms) // eslint-disable-line @typescript-eslint/no-explicit-any
const TONE = { up: 'text-radar-cyan', down: 'text-amber-glow', flat: 'text-paper-faint' } as const
const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
const td = 'px-2 py-1.5 align-top'
const mono = { fontVariantNumeric: 'tabular-nums' } as const

function Loading() { return <div className="h-24 bg-ink-800 animate-pulse" /> }
function Failed({ what }: { what: string }) { return <p className="font-mono text-[12px] text-radar-red">Couldn’t load {what}</p> }

function periodLabel(days: number | undefined) { return days ? `last ${days} days` : 'this period' }

export function DeskToday() {
  return <DeskFrame tab="today"><Body /></DeskFrame>
}

function Body() {
  const { pub, q, filters, search } = useDeskState('today')
  const p = pub as DeskPub
  const fkey = filters.map((f) => `${f.key}=${f.value}`).join('&')
  const key = [pub, q.period, q.from, q.to, q.compare, q.group, fkey]
  const ladder = useQuery({ queryKey: ['desk', 'ladder', ...key], queryFn: () => deskApi.ladder(p, q), retry: false })
  const tiles = useQuery({ queryKey: ['desk', 'tiles', ...key], queryFn: () => deskApi.tiles(p, q), retry: false })
  const titles = useQuery({ queryKey: ['desk', 'titles', ...key], queryFn: () => deskApi.titles(p, q), retry: false })
  const movers = useQuery({ queryKey: ['desk', 'movers', ...key], queryFn: () => deskApi.todayMovers(p, q), retry: false })
  const changes = useQuery({ queryKey: ['desk', 'changes', ...key], queryFn: () => deskApi.todayChanges(p, q), retry: false })
  const days = ladder.data?.meta.period?.days ?? tiles.data?.meta.period?.days
  const label = periodLabel(days)
  const prevLabel = days ? `prev ${days}d` : 'prev period'

  return (
    <div className="space-y-4">
      <LadderPanel query={ladder} />
      <div className="grid gap-4 grid-cols-1 xl:grid-cols-2">
        <ChangesPanel query={changes} pub={pub} search={search} />
        <RightNow pub={pub} />
      </div>
      <Tiles query={tiles} pub={pub} q={q} prevLabel={prevLabel} />
      <MoversPanel query={movers} search={search} />
      <TitlesPanel query={titles} label={label} search={search} />
    </div>
  )
}

type LadderQ = ReturnType<typeof useQuery<Awaited<ReturnType<typeof deskApi.ladder>>>>

/** The ladder is always the rolling 28 days to the period's last day, whatever period is picked. */
function LadderPanel({ query }: { query: LadderQ }) {
  const d = query.data
  const day = d && d.unavailable === undefined ? d.day : null
  const label = day ? `28 days to ${fmtDay(day, { month: 'short', day: 'numeric' })}` : 'last 28 days'
  return (
    <Panel label={`Reader ladder · ${label}`} accent="amber">
      {query.isError ? <Failed what="the ladder" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : !d.rolled_up ? (
        <p className="text-paper-dim">Not built yet</p>
      ) : (
        <div className="space-y-3">
          <p className="text-[12px] text-paper-faint">Visitors sit on the highest rung they reached. Engaged members are the active part of Members. Arrows show movement since the previous window.</p>
          <LadderBars rows={ladderRows(d)} period={label} />
          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-ink-500 pt-2.5 font-mono text-[12px] text-paper-dim">
            <span className="font-semibold text-paper">Step-up rates</span>
            {stepUpText(d.step_up).map((t, i) => <span key={t} className={i === 2 ? 'font-semibold text-paper' : ''}>{t}</span>)}
          </div>
          <p className="font-mono text-[12px] text-paper-faint">Not seen in 28 days: {fmtInt(d.not_seen.members)} members, {fmtInt(d.not_seen.known)} known</p>
        </div>
      )}
    </Panel>
  )
}

type ChangesQ = ReturnType<typeof useQuery<Awaited<ReturnType<typeof deskApi.todayChanges>>>>

function ChangesPanel({ query, pub, search }: { query: ChangesQ; pub: string; search: string }) {
  const d = query.data
  return (
    <Panel label="What changed since yesterday" accent="cyan">
      {query.isError ? <Failed what="what changed" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
        <div className="space-y-3">
          <GroupNote applied={d.group_applied} />
          {d.items.length > 0 && (
            <ul className="divide-y divide-ink-600">
              {d.items.slice(0, 6).map((c, i) => (
                <li key={`${c.kind}-${i}`} className="flex items-start gap-2.5 py-2">
                  <span className="shrink-0 border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-dim">{c.tag}</span>
                  <span className="min-w-0 text-paper">
                    {c.text}{' '}
                    <Link to={changeHref(c, pub, search)} className="text-amber-glow hover:underline">{c.link_text}</Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {d.quiet && <p className="text-paper-dim">Nothing unusual since yesterday.</p>}
          {d.items.length === 0 && !d.quiet && <p className="text-paper-dim">Nothing has crossed an alert line yet.</p>}
          {d.collecting.length > 0 && (
            <ul className="space-y-1 font-mono text-[12px] text-paper-faint">
              {d.collecting.map((c) => <li key={c.kind}>Collecting: {c.note}</li>)}
            </ul>
          )}
        </div>
      )}
    </Panel>
  )
}

function RightNow({ pub }: { pub: string }) {
  const avail = useQuery({ queryKey: ['desk', 'availability'], queryFn: deskApi.availability, staleTime: 60_000, retry: false })
  const live = avail.data?.titles.filter((t) => t.first_party && (pub === 'all' || t.publication === pub)).map((t) => t.publication) ?? []
  const results = useQueries({
    queries: live.map((slug) => ({
      queryKey: ['visitors-now', slug], queryFn: () => visitorsApi.now(slug), refetchInterval: poll(30_000), retry: false,
    })),
  })
  const head = (
    <span className="font-mono text-[11px] text-paper-faint">confirmed humans, last 5 min · updates every 30 s</span>
  )
  if (!avail.data) return <Panel label="Right now" accent="green" right={head}>{avail.isError ? <Failed what="Right now" /> : <Loading />}</Panel>
  if (live.length === 0) {
    const t = avail.data.titles.find((x) => x.publication === pub)
    return <Panel label="Right now" accent="green"><p className="text-paper">{t?.first_party_text || 'Starts when the new site goes live'}</p></Panel>
  }
  if (results.some((r) => r.isError)) return <Panel label="Right now" accent="green" right={head}><Failed what="Right now" /></Panel>
  if (results.some((r) => !r.data)) return <Panel label="Right now" accent="green" right={head}><Loading /></Panel>
  const sum = sumNow(live.map((slug, i) => ({ pub: slug, data: results[i].data! })))
  const links = nowLinks(pub, sum.top_pages)
  const stat = (n: number, text: string) => (
    <div>
      <div className="font-mono text-[26px] font-semibold leading-none text-paper" style={mono}>{fmtInt(n)}</div>
      <div className="mt-1 text-[12px] text-paper-faint">{text}</div>
    </div>
  )
  return (
    <Panel label="Right now" accent="green" right={head}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-x-7 gap-y-2">
          {stat(sum.active_visitors, 'people reading')}
          {stat(sum.members_in_issue, 'members in an issue')}
          {stat(sum.in_checkout, 'in checkout')}
          <span className="font-mono text-[12px] text-paper-faint" title="Visits not yet confirmed as human; not counted above">{fmtInt(sum.unconfirmed)} unconfirmed</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] text-[13px]">
            <thead><tr><th className={th}>Reading now</th><th className={th}>Came from</th><th className={`${th} text-right`}>People</th></tr></thead>
            <tbody>
              {sum.top_pages.map((r, i) => (
                <tr key={`${r.pub}-${r.path}`} className="border-t border-ink-600">
                  <td className={td}>
                    <Link to={links[i].href} title={r.path} className="block max-w-[260px] truncate text-amber-glow hover:underline">{links[i].label}</Link>
                    {pub === 'all' && r.pub && <span className="font-mono text-[11px] text-paper-faint">{pubLabel(r.pub)}</span>}
                  </td>
                  <td className={`${td} text-paper-faint`}><span className="block max-w-[160px] truncate" title={r.source ?? ''}>{r.source ?? '—'}</span></td>
                  <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.visitors)}</td>
                </tr>
              ))}
              {sum.top_pages.length === 0 && <tr><td colSpan={3} className={`${td} text-paper-faint`}>Nobody on the site right now</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  )
}

type TilesQ = ReturnType<typeof useQuery<Awaited<ReturnType<typeof deskApi.tiles>>>>

export function TileCard({ name, compared, sub, context, prevLabel, unit, group }: { name: string; compared: Compared; sub?: string | null; context?: string | null; prevLabel: string; unit?: 'positions'; group?: DeskGroup }) {
  const pos = unit === 'positions'
  const chg = (c: Compared) => (pos ? positionChange(c) : changeText(c, unit))
  const ch = chg(compared)
  const ly = pos ? (compared.last_year === null || compared.last_year === undefined ? '— (no data yet)' : compared.last_year.toFixed(1)) : lastYearText(compared)
  const lyTone = compared.last_year_change_pct === null || compared.last_year_change_pct === undefined ? 'flat'
    : compared.last_year_change_pct > 0 ? 'up' : compared.last_year_change_pct < 0 ? 'down' : 'flat'
  const v = compared.value
  const big = v === null ? '—' : pos ? v.toFixed(1) : tileValueText(v, compared.points, group)
  const lyChange = compared.last_year_change_pct === null || compared.last_year_change_pct === undefined ? null : chg({ ...compared, change: compared.last_year_change, change_pct: compared.last_year_change_pct })
  return (
    <section className="min-w-0 border border-ink-500 bg-ink-800 p-4">
      <h3 className="label">{name}</h3>
      <div className="mt-1 font-mono text-[28px] font-semibold leading-tight text-paper" style={mono}>{big}</div>
      <div className="mt-1.5 font-mono text-[13px]" style={mono}><span className={TONE[ch.tone]}>{ch.text}</span> <span className="text-paper-faint">vs {prevLabel}</span></div>
      <div className="font-mono text-[13px]" style={mono}>
        {lyChange && <span className={TONE[lyTone]}>{lyChange.text} </span>}
        <span className="text-paper-faint">{lyChange ? 'vs last year' : `last year ${ly}`}</span>
      </div>
      <div className="mt-1.5 text-[12px] text-paper-faint">{[verdictText(compared.verdict), sub, context].filter(Boolean).join(' · ')}</div>
    </section>
  )
}

function Tiles({ query, pub, q, prevLabel }: { query: TilesQ; pub: string; q: ReturnType<typeof useDeskState>['q']; prevLabel: string }) {
  const d = query.data
  const notLive = d?.unavailable !== undefined
  // A title that is not first-party live still shows Google clicks (Search Console).
  const gsc = useQuery({
    queryKey: ['desk', 'tiles-gsc', pub, q.period, q.from, q.to, q.compare, q.group],
    queryFn: () => deskApi.search(pub as DeskPub, q), enabled: notLive, retry: false,
  })
  if (query.isError) return <Failed what="the tiles" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) {
    const clicks = gsc.data && gsc.data.unavailable === undefined ? gsc.data.tiles.find((t) => t.key === 'clicks') : undefined
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
        <div className="md:col-span-2 xl:col-span-3"><NotYetLive text={d.unavailable} /></div>
        {clicks && <TileCard name="Google search clicks" compared={clicks.compared} context="from Search Console" prevLabel={prevLabel} />}
      </div>
    )
  }
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
      {d.tiles.map((t: Tile) => (
        <TileCard key={t.key} name={t.label} compared={t.compared} sub={t.sub} prevLabel={prevLabel} group={q.group}
                  context={t.context ?? (t.source === 'gsc' && !t.sub ? 'from Search Console' : null)} />
      ))}
    </div>
  )
}

type MoversQ = ReturnType<typeof useQuery<Awaited<ReturnType<typeof deskApi.todayMovers>>>>

function MoverCard({ name, list, search }: { name: string; list: MoverList; search: string }) {
  const rows = [...list.up.map((m) => ({ m, tone: 'up' as const })), ...list.down.map((m) => ({ m, tone: 'down' as const }))]
  return (
    <section className="min-w-0 border border-ink-500 bg-ink-800 p-4">
      <h3 className="label">Biggest movers · {name}</h3>
      <table className="mt-2 w-full text-[13px]">
        <tbody>
          {rows.map(({ m, tone }) => (
            <tr key={`${tone}-${m.key}`} className="border-t border-ink-600">
              <td className={td}><Link to={changeHref(m, '', search)} title={m.label} className="block max-w-[240px] truncate text-amber-glow hover:underline">{m.label}</Link></td>
              <td className={`${td} text-right font-mono ${TONE[tone]}`} style={mono}>{tone === 'up' ? '▲ +' : '▼ −'}{fmtInt(Math.abs(m.change))}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td className={`${td} text-paper-faint`}>No movers yet</td></tr>}
        </tbody>
      </table>
    </section>
  )
}

function MoversPanel({ query, search }: { query: MoversQ; search: string }) {
  const d = query.data
  if (query.isError) return <Failed what="the movers" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />
  return (
    <div className="space-y-2">
      <GroupNote applied={d.group_applied} />
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <MoverCard name="Sources" list={d.sources} search={search} />
        <MoverCard name="Articles" list={d.articles} search={search} />
        <MoverCard name="Search terms" list={d.search_terms} search={search} />
      </div>
      <p className="font-mono text-[12px] text-paper-faint">{d.note}</p>
    </div>
  )
}

type TitlesQ = ReturnType<typeof useQuery<Awaited<ReturnType<typeof deskApi.titles>>>>

function TitlesPanel({ query, label, search }: { query: TitlesQ; label: string; search: string }) {
  const d = query.data
  const body = query.isError ? <Failed what="the all-titles table" /> : !d ? <Loading /> : d.unavailable !== undefined ? <p className="text-paper">{d.unavailable}</p> : (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-[13px]">
          <thead>
            <tr>
              <th className={th}>Title</th>
              {['People', 'Regular readers', 'Known', 'Members', 'Members active', 'Regular → Member'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
              <th className={th}>Ladder shape</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const bars = shapeBars(r.shape)
              const m = r.regular_to_member
              return (
                <tr key={r.publication} className="border-t border-ink-600">
                  <td className={`${td} font-semibold`}><Link to={deskPath(r.publication, 'today', search)} className="text-amber-glow hover:underline">{pubLabel(r.publication)}</Link></td>
                  {[r.people, r.regular, r.known, r.members, r.members_active].map((n, i) => <td key={i} className={`${td} text-right font-mono`} style={mono}>{fmtInt(n)}</td>)}
                  <td className={`${td} text-right font-mono`} style={mono}>{m.numerator === null ? '—' : rateWithCount(m.rate, m.numerator)}</td>
                  <td className={td}>
                    <div className="flex h-[26px] items-end gap-0.5" role="img" aria-label={`Ladder shape: ${r.shape.map((n) => fmtInt(n)).join(', ')}`}>
                      {bars.map((h, i) => <span key={i} className="w-3.5 bg-amber-glow opacity-75" style={{ height: `${Math.max(h * 26, h > 0 ? 1 : 0)}px` }} />)}
                    </div>
                  </td>
                </tr>
              )
            })}
            {d.rows.length === 0 && <tr><td colSpan={8} className={`${td} text-paper-faint`}>No title is live yet</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[11px] text-paper-faint">Ladder shape bars, left to right: fly-by, occasional, regular, known, member, engaged. A reader of two titles counts once per title.</p>
      {d.excluded.length > 0 && (
        <p className="font-mono text-[12px] text-paper-faint">Not included: {d.excluded.map((e) => `${pubLabel(e.publication)} (${e.reason})`).join('; ')}</p>
      )}
    </div>
  )
  return <Panel label={`All titles · ${label}`} accent="none">{body}</Panel>
}
